import { useState } from "react";
import { motion } from "framer-motion";
import { X, Minus, Plus, ArrowUpRight } from "lucide-react";

const PageText = ({ page, edition, ui }) => (
  <section className="mb-12" data-testid={`text-view-page-${page.n}`} aria-labelledby={`tv-title-${page.n}`}>
    <div className="font-label uppercase" style={{ fontSize: 10, letterSpacing: "0.25em", color: "var(--bordeaux)" }}>{ui.page} {page.n} · {edition?.sections?.[page.section]}</div>
    {page.kicker && <div className="mt-2 text-xs uppercase tracking-widest opacity-60">{page.kicker}</div>}
    <h2 id={`tv-title-${page.n}`} className="font-head font-semibold mt-2" style={{ fontSize: "2em", lineHeight: 1.05 }}>{page.title}</h2>
    {page.deck && <p className="font-head italic mt-2" style={{ fontSize: "1.2em", lineHeight: 1.3 }}>{page.deck}</p>}
    {page.body?.map((p, i) => <p key={i} className="mt-3" style={{ lineHeight: 1.65 }}>{p}</p>)}
    {page.qa?.map((x, i) => (
      <div key={i} className="mt-4">
        <p className="font-head font-semibold" style={{ fontSize: "1.15em", color: "var(--bordeaux)" }}>{x.q}</p>
        <p className="mt-1" style={{ lineHeight: 1.65 }}>{x.a}</p>
      </div>
    ))}
    {page.quote && <blockquote className="font-head italic mt-4 pl-4" style={{ fontSize: "1.3em", borderLeft: "2px solid var(--bordeaux)" }}>“{page.quote}”{page.quote_by && <footer className="text-xs not-italic mt-1 opacity-70">— {page.quote_by}</footer>}</blockquote>}
    {page.items?.map((it, i) => (
      <div key={i} className="mt-4">
        <h3 className="font-head font-semibold" style={{ fontSize: "1.2em" }}>{it.title}</h3>
        {it.meta && <div className="text-xs uppercase tracking-widest opacity-60">{it.meta}</div>}
        {it.text && <p className="mt-1" style={{ lineHeight: 1.6 }}>{it.text}</p>}
        {it.url && <a href={it.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest mt-1" style={{ color: "var(--bordeaux)" }}>Link <ArrowUpRight size={12} /></a>}
      </div>
    ))}
    {page.images?.map((im, i) => <p key={i} className="mt-2 text-sm opacity-70">Image: {im.caption}</p>)}
    {page.toc?.length > 0 && <ul className="mt-3 list-none">{page.toc.map((t) => <li key={t.page} className="py-1 border-b" style={{ borderColor: "var(--platinum)" }}>{String(t.page).padStart(2, "0")} — {t.label} · <span className="italic opacity-70">{t.section}</span></li>)}</ul>}
    {page.ctas?.length > 0 && (
      <div className="flex flex-wrap gap-2 mt-4">
        {page.ctas.map((c, i) => <a key={i} href={c.url} target="_blank" rel="noopener noreferrer" className="btn-bordeaux" style={{ fontSize: 10 }}>{c.label}</a>)}
      </div>
    )}
    {page.credits?.length > 0 && <div className="mt-4 text-xs opacity-60">{page.credits.map((c, i) => <div key={i}>{c}</div>)}</div>}
    {page.note && <p className="mt-2 text-xs italic opacity-50">{page.note}</p>}
  </section>
);

export const TextView = ({ pages, edition, ui, onClose }) => {
  const [size, setSize] = useState(16);
  return (
    <motion.aside className="text-panel" initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 28, stiffness: 240 }} role="dialog" aria-label={ui.textView} data-testid="text-view-panel" lang={edition?.lang}>
      <div className="sticky top-0 flex items-center justify-between px-6 py-3" style={{ background: "var(--cream)", borderBottom: "1px solid var(--platinum)", zIndex: 2 }}>
        <div className="font-label uppercase" style={{ fontSize: 10, letterSpacing: "0.25em" }}>{ui.textView}</div>
        <div className="flex items-center gap-1">
          <span className="text-xs mr-2 opacity-60">{ui.fontSize}</span>
          <button className="reader-btn" style={{ color: "var(--charcoal)", width: 34, height: 34 }} onClick={() => setSize((s) => Math.max(13, s - 1))} data-testid="text-view-font-dec" aria-label="Decrease text size"><Minus size={14} /></button>
          <span className="text-xs w-6 text-center" data-testid="text-view-font-size">{size}</span>
          <button className="reader-btn" style={{ color: "var(--charcoal)", width: 34, height: 34 }} onClick={() => setSize((s) => Math.min(26, s + 1))} data-testid="text-view-font-inc" aria-label="Increase text size"><Plus size={14} /></button>
          <button className="reader-btn ml-2" style={{ color: "var(--charcoal)", width: 34, height: 34 }} onClick={onClose} title={ui.close} data-testid="btn-close-text-view"><X size={16} /></button>
        </div>
      </div>
      <div className="px-6 sm:px-10 py-8" style={{ fontSize: size }}>
        {pages.map((p) => <PageText key={p.n} page={p} edition={edition} ui={ui} />)}
      </div>
    </motion.aside>
  );
};
